import { useCallback, useEffect, useRef, useState, type MutableRefObject } from "react";
import { AuthorizationError, OperationError } from "@/shared/errors/auth-errors";
import { frontendErrorsApi, FrontendErrorsContractError } from "@/features/observability/api/frontend-errors.api";
import type {
    FrontendErrorEvent,
    FrontendErrorsEventsParams,
    FrontendErrorsGroups,
    FrontendErrorsGroupsParams,
    FrontendErrorsPopulationParams,
    FrontendErrorsQueryParams,
    FrontendErrorsSeries,
    FrontendErrorsSourceState,
    FrontendErrorsSourceStatus,
    FrontendErrorsSummary,
} from "@/features/observability/types/frontend-errors.types";

export type FrontendErrorsStatus = "idle" | "loading" | "ok" | "degraded" | "unavailable" | "forbidden" | "error";

export type FrontendErrorsErrorCode = "contract" | "forbidden" | "unavailable" | "request";

export type FrontendErrorsError = {
    code: FrontendErrorsErrorCode;
    message: string;
};

export type FrontendErrorsResource<T> = {
    status: FrontendErrorsStatus;
    data: T | null;
    error: FrontendErrorsError | null;
    refresh: () => void;
};

export type FrontendErrorsEventsResource = {
    status: FrontendErrorsStatus;
    events: FrontendErrorEvent[] | null;
    asOf: number | null;
    error: FrontendErrorsError | null;
    hasMore: boolean;
    loadingMore: boolean;
    loadMore: () => void;
    refresh: () => void;
};

// httpClient turns every non-public 5xx into OperationError(`HTTP <status>`); the reader's 503 is its
// "source unavailable" answer, never a generic failure.
const SOURCE_UNAVAILABLE_MESSAGE = "HTTP 503";

const STATUS_BY_SOURCE: Record<FrontendErrorsSourceStatus, FrontendErrorsStatus> = {
    ok: "ok",
    degraded: "degraded",
    unknown: "unavailable",
};

const STATUS_BY_ERROR: Record<FrontendErrorsErrorCode, FrontendErrorsStatus> = {
    contract: "error",
    forbidden: "forbidden",
    unavailable: "unavailable",
    request: "error",
};

const toFrontendErrorsError = (error: unknown): FrontendErrorsError => {
    if (error instanceof FrontendErrorsContractError) return { code: "contract", message: error.message };
    if (error instanceof AuthorizationError) return { code: "forbidden", message: error.message };
    if (error instanceof OperationError && error.message === SOURCE_UNAVAILABLE_MESSAGE) {
        return { code: "unavailable", message: error.message };
    }
    if (error instanceof Error) return { code: "request", message: error.message };
    return { code: "request", message: "The frontend errors source could not be reached" };
};

const beginRequest = (
    requestSeqRef: MutableRefObject<number>,
    controllerRef: MutableRefObject<AbortController | null>,
): { seq: number; controller: AbortController } => {
    const seq = requestSeqRef.current + 1;
    requestSeqRef.current = seq;
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    return { seq, controller };
};

// Unmount or new params: whatever is in flight, first page or loadMore, is cancelled and its answer ignored.
const cancelRequest = (
    requestSeqRef: MutableRefObject<number>,
    controllerRef: MutableRefObject<AbortController | null>,
): void => {
    requestSeqRef.current += 1;
    controllerRef.current?.abort();
};

type Snapshot<T> = {
    key: string | null;
    status: FrontendErrorsStatus;
    data: T | null;
    error: FrontendErrorsError | null;
};

// Shared with platform errors (vb-metrics#47): only `sourceStatus` drives the state machine.
export const useFrontendErrorsResource = <P, T extends Pick<FrontendErrorsSourceState, "sourceStatus">>(
    params: P | null,
    fetcher: (params: P, signal: AbortSignal) => Promise<T>,
): FrontendErrorsResource<T> => {
    const key = params === null ? null : JSON.stringify(params);
    const paramsRef = useRef(params);
    paramsRef.current = params;
    const fetcherRef = useRef(fetcher);
    fetcherRef.current = fetcher;
    const requestSeqRef = useRef(0);
    const controllerRef = useRef<AbortController | null>(null);

    const [snapshot, setSnapshot] = useState<Snapshot<T>>({ key: null, status: "idle", data: null, error: null });

    const load = useCallback(async (targetKey: string, targetParams: P): Promise<void> => {
        const { seq, controller } = beginRequest(requestSeqRef, controllerRef);
        setSnapshot((previous) =>
            previous.key === targetKey
                ? { ...previous, status: "loading", error: null }
                : { key: targetKey, status: "loading", data: null, error: null },
        );
        try {
            const data = await fetcherRef.current(targetParams, controller.signal);
            if (seq !== requestSeqRef.current) return;
            setSnapshot({ key: targetKey, status: STATUS_BY_SOURCE[data.sourceStatus], data, error: null });
        } catch (error) {
            if (seq !== requestSeqRef.current || controller.signal.aborted) return;
            const failure = toFrontendErrorsError(error);
            // A failed answer never keeps the previous numbers on screen: they would read as current.
            setSnapshot({ key: targetKey, status: STATUS_BY_ERROR[failure.code], data: null, error: failure });
        }
    }, []);

    useEffect(() => {
        const current = paramsRef.current;
        if (key === null || current === null) return undefined;
        void load(key, current);
        return () => cancelRequest(requestSeqRef, controllerRef);
    }, [key, load]);

    const refresh = useCallback((): void => {
        const current = paramsRef.current;
        if (key === null || current === null) return;
        void load(key, current);
    }, [key, load]);

    if (key === null) return { status: "idle", data: null, error: null, refresh };
    if (snapshot.key !== key) return { status: "loading", data: null, error: null, refresh };
    return { status: snapshot.status, data: snapshot.data, error: snapshot.error, refresh };
};

export const useFrontendErrorsSummary = (params: FrontendErrorsQueryParams | null): FrontendErrorsResource<FrontendErrorsSummary> =>
    useFrontendErrorsResource(params, (query, signal) => frontendErrorsApi.getSummary(query, signal));

export const useFrontendErrorsSeries = (params: FrontendErrorsPopulationParams | null): FrontendErrorsResource<FrontendErrorsSeries> =>
    useFrontendErrorsResource(params, (query, signal) => frontendErrorsApi.getSeries(query, signal));

export const useFrontendErrorsGroups = (params: FrontendErrorsGroupsParams | null): FrontendErrorsResource<FrontendErrorsGroups> =>
    useFrontendErrorsResource(params, (query, signal) => frontendErrorsApi.getGroups(query, signal));

type EventsSnapshot = {
    key: string | null;
    status: FrontendErrorsStatus;
    events: FrontendErrorEvent[] | null;
    nextCursor: string | null;
    error: FrontendErrorsError | null;
    loadingMore: boolean;
};

const EMPTY_EVENTS: EventsSnapshot = { key: null, status: "idle", events: null, nextCursor: null, error: null, loadingMore: false };

const appendUnseen = (current: FrontendErrorEvent[], page: FrontendErrorEvent[]): FrontendErrorEvent[] => {
    const seen = new Set(current.map((event) => event.receiptId));
    const added: FrontendErrorEvent[] = [];
    page.forEach((event) => {
        if (seen.has(event.receiptId)) return;
        seen.add(event.receiptId);
        added.push(event);
    });
    return current.concat(added);
};

// The cursor is bound to the query it was issued for, asOf included: every page after the first is asked
// with the first page's params, so a later asOf can never be paired with an earlier cursor.
export const useFrontendErrorsEvents = (params: FrontendErrorsEventsParams | null): FrontendErrorsEventsResource => {
    const key = params === null ? null : JSON.stringify(params);
    const paramsRef = useRef(params);
    paramsRef.current = params;
    const queryRef = useRef<{ key: string; params: FrontendErrorsEventsParams } | null>(null);
    const requestSeqRef = useRef(0);
    const controllerRef = useRef<AbortController | null>(null);
    const [snapshot, setSnapshot] = useState<EventsSnapshot>(EMPTY_EVENTS);
    const snapshotRef = useRef(snapshot);
    snapshotRef.current = snapshot;

    const loadFirst = useCallback(async (targetKey: string, targetParams: FrontendErrorsEventsParams): Promise<void> => {
        queryRef.current = { key: targetKey, params: targetParams };
        const { seq, controller } = beginRequest(requestSeqRef, controllerRef);
        setSnapshot({ ...EMPTY_EVENTS, key: targetKey, status: "loading" });
        try {
            const page = await frontendErrorsApi.getEvents({ ...targetParams, cursor: null }, controller.signal);
            if (seq !== requestSeqRef.current) return;
            setSnapshot({
                ...EMPTY_EVENTS,
                key: targetKey,
                status: STATUS_BY_SOURCE[page.sourceStatus],
                events: page.events === null ? null : appendUnseen([], page.events),
                nextCursor: page.events === null ? null : page.nextCursor,
            });
        } catch (error) {
            if (seq !== requestSeqRef.current || controller.signal.aborted) return;
            const failure = toFrontendErrorsError(error);
            setSnapshot({ ...EMPTY_EVENTS, key: targetKey, status: STATUS_BY_ERROR[failure.code], error: failure });
        }
    }, []);

    useEffect(() => {
        const current = paramsRef.current;
        if (key === null || current === null) {
            queryRef.current = null;
            return undefined;
        }
        void loadFirst(key, current);
        return () => cancelRequest(requestSeqRef, controllerRef);
    }, [key, loadFirst]);

    const loadMore = useCallback((): void => {
        const query = queryRef.current;
        const current = snapshotRef.current;
        if (query === null || current.key !== query.key || current.loadingMore || current.events === null) return;
        const cursor = current.nextCursor;
        if (cursor === null) return;
        const { seq, controller } = beginRequest(requestSeqRef, controllerRef);
        const accepted = (): boolean => seq === requestSeqRef.current;
        setSnapshot((previous) => ({ ...previous, loadingMore: true, error: null }));
        frontendErrorsApi.getEvents({ ...query.params, cursor }, controller.signal).then(
            (page) => {
                if (!accepted()) return;
                const pageEvents = page.events;
                if (pageEvents === null) {
                    // The source went away mid-pagination: the pages already shown stay valid for their asOf.
                    setSnapshot((previous) => ({
                        ...previous,
                        loadingMore: false,
                        error: { code: "unavailable", message: "Frontend errors source unavailable" },
                    }));
                    return;
                }
                setSnapshot((previous) => ({
                    ...previous,
                    loadingMore: false,
                    status: previous.status === "degraded" || page.sourceStatus === "degraded" ? "degraded" : previous.status,
                    events: appendUnseen(previous.events === null ? [] : previous.events, pageEvents),
                    nextCursor: page.nextCursor,
                }));
            },
            (error: unknown) => {
                if (!accepted() || controller.signal.aborted) return;
                const failure = toFrontendErrorsError(error);
                if (failure.code === "forbidden") {
                    setSnapshot((previous) => ({ ...EMPTY_EVENTS, key: previous.key, status: "forbidden", error: failure }));
                    return;
                }
                setSnapshot((previous) => ({ ...previous, loadingMore: false, error: failure }));
            },
        );
    }, []);

    const refresh = useCallback((): void => {
        const current = paramsRef.current;
        if (key === null || current === null) return;
        void loadFirst(key, current);
    }, [key, loadFirst]);

    if (key === null || params === null) {
        return { status: "idle", events: null, asOf: null, error: null, hasMore: false, loadingMore: false, loadMore, refresh };
    }
    if (snapshot.key !== key) {
        return { status: "loading", events: null, asOf: params.asOf, error: null, hasMore: false, loadingMore: false, loadMore, refresh };
    }
    return {
        status: snapshot.status,
        events: snapshot.events,
        asOf: params.asOf,
        error: snapshot.error,
        hasMore: snapshot.nextCursor !== null,
        loadingMore: snapshot.loadingMore,
        loadMore,
        refresh,
    };
};
